import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../UserContext";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Link,
  Button
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getJobOrder, putJobOrder } from "../API/jobOrderAPI";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";

import BaseFormWrapper from "../../components/shared/BaseFormWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from "@mui/icons-material/Circle";
import { JOB_DESCRIPTIONS, JOB_TYPES } from "../JobOrders/utils/JobOrderConstants";

function EditTransmittalForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobOrder, setJobOrder] = useState({});
  const [originalJobOrder, setOriginalJobOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  // Get returnTo parameter
  const returnTo = new URLSearchParams(location.search).get('returnTo');

  useEffect(() => {
    async function fetch() {
      const data = await getJobOrder(id);
      console.log('Job Order Data:', data);
      console.log('Status value:', data?.status);
      setJobOrder(data);
      setOriginalJobOrder(data);
    }
    fetch();
  }, [id]);

  // const handleInputChange = (field, value) => {
  //   setJobOrder(prev => {
  //     const newState = {
  //       ...prev,
  //       [field]: value
  //     };

  //     // If changing the start date, ensure end date is not before start date
  //     if (field === 'job_date' && prev.end_date && value && value > prev.end_date) {
  //       newState.end_date = value; // Set end date to start date if it's earlier
  //     }

  //     // If changing the end date, ensure it's not before start date
  //     if (field === 'end_date' && prev.job_date && value && value < prev.job_date) {
  //       return prev; // Don't update if end date is before start date
  //     }

  //     return newState;
  //   });
  // };
  const handleInputChange = (field, value) => {
    setJobOrder(prev => ({ ...prev, [field]: value }));
  };


  // Convert booleans to the string values your lambdas expect ("True"/"False")
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) return obj.map(convertBooleans);
    if (obj && typeof obj === "object") {
      return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, convertBooleans(v)]));
    }
    if (typeof obj === "boolean") return obj ? "True" : "False";
    return obj;
  };

  // Builds the edited parameters we want rendered on the PDF
  const buildUpdatedParams = () => {
    // Keep only the fields Transmittal cares about (same spirit as your handleSave)
    // and derive a readable technicians list for the PDF.
    const techNames = (jobOrder?.technicians || []).map(t => `${t.firstname} ${t.lastname}`);

    return {
      status: jobOrder?.status || "",
      job_date: jobOrder?.job_date || "",
      // end_date: jobOrder?.end_date || "",
      start_time: jobOrder?.start_time || "",
      end_time: jobOrder?.end_time || "",
      transmittal_notes: jobOrder?.transmittal_notes || "",
      accomplished_by_technician: techNames,
      technician_signatory: jobOrder?.technician_signatory || "",
      acknowledgedClientSignatory: jobOrder?.acknowledgedClientSignatory || "",
      accomplishedSignature: jobOrder?.accomplishedSignature || "",
      acknowledgedSignature: jobOrder?.acknowledgedSignature || "",
    };
  };


  const handleSave = async () => {
    try {
      setIsLoading(true);

      // Build the payload (CRITICAL: pass edited values as `form`)
      const updatedJobOrderParameters = buildUpdatedParams();
      const payload = convertBooleans({
        jobOrder,
        updatedJobOrderParameters,
        form: updatedJobOrderParameters, // <-- makes the receipt/transmittal reflect edits
      });

      // Generate doc(s)
      await submitDocumentFieldMapper(payload);

      // Compose the single doc URL (same convention as Transmittal)
      const receipts_url = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`;

      // Persist link + keep local state in sync
      await patchJobOrderLink(jobOrder.id, { receipts_url });
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      setJobOrder(prev => ({ ...prev, receipts_url }));

      setSuccessMessage("Service report updated and documents regenerated successfully!");      
    } catch (error) {
      console.error("Error updating job order:", error);
      setErrorMessage("Failed to update transmittal form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  const statusOptions = [
    "Not Done",
    "Rescheduled", 
    "Done",
    "Cancelled By Client"
  ];

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Edit Service Report"
      isLoading={isLoading}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
    >
      <Grid container>
        {/* Basic Information Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography variant="h6">
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <TextField
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Job Order Status</InputLabel>
                    <Select
                      value={jobOrder?.status || ""}
                      label="Job Order Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      displayEmpty
                    >
                      {jobOrder?.status && !statusOptions.includes(jobOrder.status) && (
                        <MenuItem value={jobOrder.status}>
                          {jobOrder.status}
                        </MenuItem>
                      )}
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Client ID"
                    value={jobOrder?.client?.id || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={12} xl={10}>
                      <TextField
                        label="Job Site Address"
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder?.client?.address?.house_unit_street || ""}, ${jobOrder?.client?.address?.barangay || ""}, ${jobOrder?.client?.address?.city || ""}, ${jobOrder?.client?.address?.metro_province || ""} ${jobOrder?.client?.address?.zipcode || ""}`
                            : ""
                        }
                        fullWidth
                        disabled
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={12} xl={2}>
                      <TextField
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Person"
                    value={
                      jobOrder?.client?.contact
                        ? `${jobOrder?.client?.contact?.lastname}, ${jobOrder?.client?.contact?.firstname}`
                        : ""
                    }
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Email"
                    value={jobOrder?.client?.contact?.email?.email || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={12} xl={4}>
                  <TextField
                    label="Contact Number"
                    value={
                      jobOrder?.client?.contact?.number
                        ? `${jobOrder?.client?.contact?.number?.type}: ${jobOrder?.client?.contact?.number?.number}`
                        : ""
                    }
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} md={12} lg={4} xl={4}>
                  <TextField
                    label="Location Type"
                    value={jobOrder?.client?.location_type || ""}
                    fullWidth
                    disabled
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Job Type</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Job Description</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={jobOrder?.special_notes || ""}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section - EDITABLE */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrder?.job_date || ""}
                    fullWidth
                    disabled
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrder?.start_time || ""}
                    onChange={(e) => handleInputChange('start_time', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrder?.end_date || ""}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        min: jobOrder?.job_date || undefined
                      }
                    }}
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrder?.end_time || ""}
                    onChange={(e) => handleInputChange('end_time', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Transmittal Notes - EDITABLE */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Notes"
                value={jobOrder.transmittal_notes || ''}
                onChange={(e) => handleInputChange('transmittal_notes', e.target.value)}
                fullWidth 
                multiline 
                rows={6}
                size="small" 
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Accomplished By and Acknowledged By Signature Section */}
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {/* Service Accomplished by Section */}
                  <Grid item xs={12} md={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                          Service Accomplished by:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Grid container spacing={1}>
                          {jobOrder?.technicians?.length > 0 ? (
                            jobOrder?.technicians.map((technician, index) => (
                              <Grid
                                item
                                xs={12}
                                md={12}
                                lg={12}
                                xl={12}
                                key={technician.id}
                                mb={2}
                              >
                                <TextField
                                  label={`Technician ${index + 1}`}
                                  variant="outlined"
                                  fullWidth
                                  value={`${technician.firstname} ${technician.lastname}`}
                                  disabled
                                  size="small"
                                />
                              </Grid>
                            ))
                          ) : (
                            <Grid item xs={12} md={12} lg={12} xl={12}>
                              <TextField
                                label="Technician"
                                variant="outlined"
                                fullWidth
                                value="No Technicians Available"
                                InputProps={{
                                  readOnly: true,
                                }}
                                size="small"
                              />
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={6}>                          
                        {/* Accomplished Signature */}
                        <Grid item xs={12}>
                          {jobOrder.accomplishedSignature && (
                            <Box
                              component="img"
                              src={jobOrder.accomplishedSignature}
                              alt="Accomplished Signature"
                              sx={{
                                width: "100%",
                                maxWidth: 400,
                                height: 120,
                                objectFit: "contain",
                                border: "1px solid rgba(0,0,0,0.3)",
                                mb: 2,
                              }}
                            />
                          )}
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Technician Signatory dropdown, right below the signature */}
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel id="technician-signatory-label">
                          Technician Signatory
                        </InputLabel>
                        <Select
                          labelId="technician-signatory-label"
                          value={jobOrder.technician_signatory || ""}
                          label="Technician Signatory"
                          disabled
                        >
                          {jobOrder.technicians?.map((tech) => (
                            <MenuItem
                              key={tech.id}
                              value={`${tech.firstname} ${tech.lastname}`}
                            >
                              {`${tech.firstname} ${tech.lastname}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                    {!isTechnician && jobOrder?.technician_signature_uploaded_at && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            🔵 Technician Signature Uploaded At:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                            {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Acknowledged Section */}
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography variant="subtitle2"
                              sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    For Client Only
                  </Typography>
                }
                sx={{ px: 2, pt: 2, pb: 0 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                  Acknowledged By:
                </Typography>
                {jobOrder.acknowledgedSignature && (
                  <Box
                    component="img"
                    src={jobOrder.acknowledgedSignature}
                    alt="Acknowledged Signature"
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                      height: 120,
                      objectFit: "contain",
                      border: "1px solid rgba(0,0,0,0.3)",
                      mb: 2,
                    }}
                  />
                )}
                <TextField
                  label="Client Representative"
                  variant="outlined"
                  fullWidth
                  size="small"
                  value={jobOrder.acknowledgedClientSignatory || ""}
                  disabled
                />

                {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder?.client_signature_uploaded_at && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                      🟢 Client Signature Uploaded At:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                      {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      })}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {jobOrder?.receipts_url ? (
                  <>
                    {jobOrder?.receipts_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={`${jobOrder.receipts_url}?v=${encodeURIComponent(jobOrder.timestamp_update || Date.now())}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder?.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Action Buttons Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                window.location.href = returnTo;
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isLoading}
            sx={{
              fontSize: "0.875rem",
            }}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default EditTransmittalForm;
